import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import InputSystem from '../InputSystem';
import ModalSystem from '../ModalSystem/ModalSystem';
import ButtonSystem from '../ButtonSystem';
import { WarningNewIcon } from '../../../icons';
import { Colors } from '../../../constants/colors.constants';

const ICON_SIZE = 48;

const ConfirmationModalSystem = ({
  visible,
  setVisible,
  message,
  phrase,
  confirmButtonText,
  onConfirm
}) => {
  const [value, setValue] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (visible) {
      setValue('');
      setIsValid(false);
      setShowError(false);
      setIsLoading(false);
    }
  }, [visible]);

  useEffect(() => {
    setIsValid(value.trim() === phrase);
    setShowError(false);
  }, [value]);

  const handleConfirm = async () => {
    setIsLoading(true);
    await onConfirm();
    setIsLoading(false);
    setVisible(false);
  };

  const contentModal = () => (
    <div className="confirmation-modal-system">
      <div className="confirmation-modal-system__icon">
        <WarningNewIcon color={Colors.YELLOW} size={ICON_SIZE} />
      </div>
      <div className="confirmation-modal-system__message">{message}</div>

      <div className="confirmation-modal-system__validate">
        <div className="confirmation-modal-system__validate-instruction">
          <span
            dangerouslySetInnerHTML={{
              __html: t('confirmation_modal.instruction', {
                phrase: `<span class="confirmation-modal-system__validate-instruction-phrase">${phrase}</span>`
              })
            }}
          />
        </div>
        <div className="confirmation-modal-system__validate-input">
          <InputSystem
            theme="dark"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => setShowError(value.trim() !== phrase)}
            isError={showError}
          />
        </div>
      </div>

      <div className="confirmation-modal-system__actions">
        <ButtonSystem type="secondary" onClick={() => setVisible(false)}>
          {t('cancel')}
        </ButtonSystem>
        <ButtonSystem
          disabled={!isValid}
          onClick={handleConfirm}
          loading={isLoading}>
          {confirmButtonText || t('confirmation_modal.confirm')}
        </ButtonSystem>
      </div>
    </div>
  );

  return ModalSystem({
    theme: 'dark',
    width: 480,
    showTitle: false,
    showIconTitle: true,
    visible,
    setVisible,
    centered: true,
    children: contentModal(),
    onCancelFunction: () => setVisible(false)
  });
};

ConfirmationModalSystem.propTypes = {
  message: PropTypes.string.isRequired,
  phrase: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  confirmButtonText: PropTypes.string,
  onConfirm: PropTypes.func.isRequired
};

export default ConfirmationModalSystem;
